import { Box, Card, CardContent, Chip, Link, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuthStore } from '../stores/auth';
import { Roles } from '../auth/roles';

const colSx = { flex: '1 1 200px', minWidth: 0 };
const labelSx = { textTransform: 'uppercase' as const, letterSpacing: 0.5 };

export function HomePage() {
  const user = useAuthStore((s) => s.user);
  const isServiceProvider = useAuthStore((s) => s.isServiceProvider)();
  // "Try the API" is a developer / diagnostic surface — Swagger, Scalar, raw
  // endpoint links. Hide it from plain User accounts so the home screen reads
  // as a real product landing page for them, not an internals dashboard.
  const canSeeApiLinks = useAuthStore(
    (s) => s.hasRole(Roles.Admin, Roles.AdminService, Roles.ServiceProvider),
  );

  return (
    <Stack spacing={2}>
      <Card>
        <CardContent>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            Welcome, {user?.username ?? 'user'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Signed in via SQL Server authentication
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
            <Box sx={colSx}>
              <Typography variant="caption" color="text.secondary" sx={labelSx}>User id</Typography>
              <Typography sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 13, wordBreak: 'break-all' }}>
                {user?.id}
              </Typography>
            </Box>
            <Box sx={colSx}>
              <Typography variant="caption" color="text.secondary" sx={labelSx}>Email</Typography>
              <Typography sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}>{user?.email}</Typography>
            </Box>
            <Box sx={colSx}>
              <Typography variant="caption" color="text.secondary" sx={labelSx}>Roles</Typography>
              <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                {(user?.roles ?? []).map((r) => (
                  <Chip key={r} label={r} size="small" variant="outlined" />
                ))}
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {canSeeApiLinks && (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>You are signed in</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Try the API:</Typography>
            <Stack spacing={0.5}>
              <Link href="/swagger/index.html" target="_blank" rel="noopener">Swagger UI</Link>
              <Link href="/scalar/v1" target="_blank" rel="noopener">Scalar API reference</Link>
              <Link href="/health" target="_blank" rel="noopener">Health</Link>
              <Link href="/customers" target="_blank" rel="noopener">Customers endpoint</Link>
              {isServiceProvider && <Link component={RouterLink} to="/logs">Logs (App Insights demo)</Link>}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}
